import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Progress } from './ui/progress';
import { toast } from 'sonner';
import { 
  User, Target, Activity, Dumbbell, Heart, AlertTriangle,
  ChevronRight, ChevronLeft, Loader2, CheckCircle2, Scale,
  Droplets, Flame, Zap, LogOut
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GOALS = [
  { value: 'perder_grasa', label: 'Perder grasa', icon: Flame, color: 'text-orange-400' },
  { value: 'ganar_musculo', label: 'Ganar masa muscular', icon: Dumbbell, color: 'text-blue-400' },
  { value: 'salud_general', label: 'Mejorar mi salud general', icon: Heart, color: 'text-red-400' },
  { value: 'objetivo_guardian', label: 'Objetivo Guardian', icon: Target, color: 'text-primary' },
  { value: 'atletico_funcional', label: 'Ser atlético funcional', icon: Zap, color: 'text-amber-400' },
];

const EXPERIENCE_LEVELS = [
  { value: 'principiante', label: 'Principiante', desc: '< 6 meses de experiencia' },
  { value: 'intermedio', label: 'Intermedio', desc: '6 meses - 2 años' },
  { value: 'avanzado', label: 'Avanzado', desc: '> 2 años de experiencia' },
];

const GYM_ACCESS = [
  { value: 'completo', label: 'Gimnasio completo', desc: 'Acceso a todo el equipamiento' },
  { value: 'limitado', label: 'Gimnasio limitado', desc: 'Equipamiento básico' },
  { value: 'casa', label: 'Entreno en casa', desc: 'Sin acceso a gimnasio' },
];

const PATHOLOGIES = [
  { value: 'ninguna', label: 'Ninguna' },
  { value: 'endometriosis', label: 'Endometriosis', special: true, description: 'Plan especializado disponible' },
  { value: 'tep', label: 'Tromboembolia Pulmonar (TEP)', special: true, description: 'Plan de recuperación especializado' },
  { value: 'espalda', label: 'Problemas de espalda (hernia, lumbalgia)' },
  { value: 'rodilla', label: 'Problemas de rodilla' },
  { value: 'hombro', label: 'Problemas de hombro' },
  { value: 'hipertension', label: 'Hipertensión' },
  { value: 'diabetes', label: 'Diabetes' },
  { value: 'cardiacos', label: 'Problemas cardíacos' },
];

const ClientOnboarding = ({ onComplete }) => {
  const { logout } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const totalSteps = 3;

  const handleLogout = () => {
    logout();
    window.location.href = '/';
  };
  
  const [formData, setFormData] = useState({
    // Sección 1: Datos fisiológicos
    age: '',
    weight: '',
    height: '',
    muscle_mass: '',
    body_fat: '',
    visceral_fat: '',
    body_water: '',
    // Sección 2: Objetivos
    goal: '',
    training_days: '',
    experience_level: '',
    gym_access: '',
    // Sección 3: Patologías
    pathologies: [],
    pathology_notes: ''
  });

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const togglePathology = (value) => {
    if (value === 'ninguna') {
      setFormData(prev => ({ ...prev, pathologies: ['ninguna'] }));
    } else {
      setFormData(prev => {
        const current = prev.pathologies.filter(p => p !== 'ninguna');
        if (current.includes(value)) {
          return { ...prev, pathologies: current.filter(p => p !== value) };
        } else {
          return { ...prev, pathologies: [...current, value] };
        }
      });
    }
  };

  const canProceed = () => {
    if (step === 1) {
      return formData.weight && formData.height;
    }
    if (step === 2) {
      return formData.goal && formData.training_days && formData.experience_level;
    }
    return true;
  };

  const handleSeniorQuickPath = async () => {
    // Validar datos mínimos
    if (!formData.weight || !formData.height) {
      toast.error('Por favor completa al menos tu peso y altura antes de continuar');
      return;
    }
    // Pre-configurar perfil Senior
    const seniorOverrides = {
      goal: 'salud_general',
      training_days: 2,
      experience_level: 'principiante',
      gym_access: 'completo',
      pathologies: (formData.pathologies && formData.pathologies.length > 0) ? formData.pathologies : ['ninguna'],
      is_senior: true,
    };
    setFormData(prev => ({ ...prev, ...seniorOverrides }));
    await handleSubmit({ ...seniorOverrides, is_senior: true });
  };

  const handleSubmit = async (overrides = {}) => {
    setLoading(true);
    try {
      const merged = { ...formData, ...overrides };
      const dataToSend = {
        weight: merged.weight ? parseFloat(merged.weight) : null,
        height: merged.height ? parseFloat(merged.height) : null,
        muscle_mass: merged.muscle_mass ? parseFloat(merged.muscle_mass) : null,
        body_fat: merged.body_fat ? parseFloat(merged.body_fat) : null,
        visceral_fat: merged.visceral_fat ? parseFloat(merged.visceral_fat) : null,
        body_water: merged.body_water ? parseFloat(merged.body_water) : null,
        goal: merged.goal,
        training_days: merged.training_days ? parseInt(merged.training_days) : null,
        experience_level: merged.experience_level,
        gym_access: merged.gym_access,
        pathologies: (merged.pathologies && merged.pathologies.length > 0) ? merged.pathologies : ['ninguna'],
        pathology_notes: merged.pathology_notes
      };

      await axios.post(`${API}/me/onboarding`, dataToSend);
      
      // Si tiene patología especial, llamar al endpoint de patología para asignación automática
      const specialPathologies = ['endometriosis', 'tep'];
      for (const pathology of specialPathologies) {
        if ((merged.pathologies || []).includes(pathology)) {
          try {
            const pathologyResponse = await axios.post(`${API}/me/onboarding-pathology?pathology=${pathology}`);
            if (pathologyResponse.data.auto_assigned_plan) {
              toast.success(`¡Plan "${pathologyResponse.data.auto_assigned_plan.name}" asignado automáticamente!`, {
                duration: 5000
              });
            }
          } catch (pathErr) {
            console.error(`Error assigning ${pathology} plan:`, pathErr);
          }
          break; // Solo asignar un plan especial
        }
      }
      
      // Si tiene entre 14-16 años, asignar plan de adolescentes automáticamente
      const age = parseInt(merged.age);
      if (age >= 14 && age <= 16) {
        try {
          const adolescentResponse = await axios.post(`${API}/me/onboarding-age-plan?age=${age}`);
          if (adolescentResponse.data.auto_assigned_plan) {
            toast.success(`¡Plan "${adolescentResponse.data.auto_assigned_plan.name}" asignado automáticamente!`, {
              duration: 5000
            });
          }
        } catch (ageErr) {
          console.error('Error assigning adolescent plan:', ageErr);
        }
      }
      
      // Si es Senior (atajo explícito) O cumple criterios: principiante + 2 días/semana + gimnasio completo
      const days = parseInt(merged.training_days);
      const isSeniorByCriteria = (days === 2 && merged.experience_level === 'principiante' && merged.gym_access === 'completo');
      let macroAssigned = false;
      if (overrides.is_senior || isSeniorByCriteria) {
        try {
          const seniorResponse = await axios.post(`${API}/me/onboarding-auto-plan`, {
            criteria: 'senior_original',
            training_days: 2,
            experience_level: 'principiante',
            gym_access: 'gimnasio_completo'
          });
          // VALIDAR que efectivamente se asignó algo (no asumir éxito por status 200)
          if (seniorResponse.data.auto_assigned_plan) {
            toast.success(`¡Plan "${seniorResponse.data.auto_assigned_plan.name}" asignado automáticamente!`, {
              duration: 5000
            });
          } else {
            // El endpoint respondió OK pero no encontró plan Senior en la DB
            toast.error(
              '⚠ No se encontró el plan Senior en la base de datos. Avisá a tu entrenador para que te lo asigne manualmente.',
              { duration: 10000 }
            );
            console.error('[senior_auto] Backend respondió OK pero auto_assigned_plan=null', seniorResponse.data);
          }
          if (seniorResponse.data.auto_assigned_macro) {
            macroAssigned = true;
            toast.success(`Macro "${seniorResponse.data.auto_assigned_macro.name}" activado · Empezás en Mes 1, Semana 1, Sesión 1`, {
              duration: 6000
            });
          } else if (seniorResponse.data.auto_assigned_plan) {
            // Tiene plan pero no macro → avisar (no crítico)
            toast.warning(
              '⚠ Plan asignado pero sin Macro de progresión. Avisá a tu entrenador para activar la progresión multi-mes.',
              { duration: 8000 }
            );
          }
        } catch (seniorErr) {
          console.error('Error assigning senior plan:', seniorErr);
          toast.error(
            '❌ No se pudo asignar el Plan Senior automáticamente. Avisá a tu entrenador.',
            { duration: 10000 }
          );
        }
      }

      // ====== AUTO-ASIGNACIÓN POR OBJETIVO (macros del builder) ======
      // Si no es senior ni se asignó nada especial, intentar macro por objetivo del cuestionario
      const GOAL_OBJECTIVES = ['perder_grasa', 'ganar_musculo', 'salud_general', 'atletico_funcional'];
      const isSpecialCase = (merged.pathologies || []).some(p => ['endometriosis', 'tep'].includes(p)) || (age >= 14 && age <= 16);
      if (!overrides.is_senior && !isSeniorByCriteria && !macroAssigned && !isSpecialCase && GOAL_OBJECTIVES.includes(merged.goal)) {
        try {
          const objResponse = await axios.post(`${API}/me/onboarding-auto-plan`, {
            criteria: 'objective',
            objective: merged.goal,
            training_days: merged.training_days,
          });
          if (objResponse.data.auto_assigned_macro) {
            toast.success(`Macro "${objResponse.data.auto_assigned_macro.name}" activado · Empezás en Mes 1, Semana 1, Sesión 1`, {
              duration: 6000
            });
          } else if (!objResponse.data.already_active_macro) {
            // Aún no hay macro para este objetivo → el entrenador asignará manualmente (no es error)
            toast.info('Tu entrenador te asignará el plan personalizado en breve 💪', { duration: 6000 });
          }
        } catch (objErr) {
          console.error('Error en auto-asignación por objetivo:', objErr);
        }
      }
      
      toast.success('¡Bienvenido a GuardiansFit!');
      if (onComplete) onComplete();
    } catch (error) {
      toast.error('Error al guardar los datos');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl bg-card border-border">
        {/* Botón cerrar sesión */}
        <div className="flex justify-end p-4 pb-0">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={handleLogout}
            className="text-muted-foreground hover:text-destructive"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Cerrar sesión
          </Button>
        </div>
        
        <CardHeader className="text-center pb-2 pt-0">
          <div className="mx-auto mb-4 w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center">
            <Target className="h-8 w-8 text-primary" />
          </div>
          <CardTitle className="text-2xl">Bienvenido a GuardiansFit</CardTitle>
          <p className="text-muted-foreground mt-2">
            Completa tu perfil para personalizar tu experiencia
          </p>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Progress */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>Paso {step} de {totalSteps}</span>
              <span>{Math.round((step / totalSteps) * 100)}%</span>
            </div>
            <Progress value={(step / totalSteps) * 100} className="h-2" />
          </div>

          {/* Step 1: Datos Fisiológicos */}
          {step === 1 && (
            <div className="space-y-6" data-testid="onboarding-step-1">
              {/* ATAJO SENIOR: botón prominente para flujo rápido */}
              <div className="rounded-lg border-2 border-amber-500/40 bg-gradient-to-br from-amber-500/10 to-orange-500/10 p-4">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                    <Heart className="h-5 w-5 text-amber-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-foreground text-sm">¿Eres Senior (60+)?</h3>
                    <p className="text-xs text-muted-foreground mt-1">
                      Completá solo peso y altura, y te asignamos el plan Senior automáticamente · 2 días/semana · empezás en Mes 1.
                    </p>
                    <Button
                      type="button"
                      variant="default"
                      size="sm"
                      onClick={handleSeniorQuickPath}
                      disabled={loading}
                      className="mt-3 bg-amber-500 hover:bg-amber-600 text-white"
                      data-testid="senior-quick-path-btn"
                    >
                      {loading ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Activando…</>
                      ) : (
                        <><CheckCircle2 className="h-4 w-4 mr-2" /> Soy Senior · Asignar plan ahora</>
                      )}
                    </Button>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 text-lg font-semibold text-foreground">
                <Scale className="h-5 w-5 text-primary" />
                Datos Fisiológicos
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Edad *</Label>
                  <Input
                    type="number"
                    placeholder="25"
                    value={formData.age}
                    onChange={(e) => updateField('age', e.target.value)}
                    className="bg-zinc-900/50 border-zinc-800"
                    data-testid="age-input"
                  />
                  {formData.age && parseInt(formData.age) >= 14 && parseInt(formData.age) <= 16 && (
                    <p className="text-xs text-amber-400">
                      Plan "Desarrollo Funcional Adolescente" se asignará automáticamente
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Peso (kg) *</Label>
                  <Input
                    type="number"
                    step="0.1"
                    placeholder="75.5"
                    value={formData.weight}
                    onChange={(e) => updateField('weight', e.target.value)}
                    className="bg-zinc-900/50 border-zinc-800"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Altura (cm) *</Label>
                  <Input
                    type="number"
                    placeholder="175"
                    value={formData.height}
                    onChange={(e) => updateField('height', e.target.value)}
                    className="bg-zinc-900/50 border-zinc-800"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Masa muscular (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    placeholder="35.0"
                    value={formData.muscle_mass}
                    onChange={(e) => updateField('muscle_mass', e.target.value)}
                    className="bg-zinc-900/50 border-zinc-800"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Grasa corporal (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    placeholder="20.0"
                    value={formData.body_fat}
                    onChange={(e) => updateField('body_fat', e.target.value)}
                    className="bg-zinc-900/50 border-zinc-800"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Grasa visceral (índice)</Label>
                  <Input
                    type="number"
                    placeholder="8"
                    value={formData.visceral_fat}
                    onChange={(e) => updateField('visceral_fat', e.target.value)}
                    className="bg-zinc-900/50 border-zinc-800"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    <Droplets className="h-4 w-4 text-blue-400" />
                    Agua corporal (%)
                  </Label>
                  <Input
                    type="number"
                    step="0.1"
                    placeholder="55.0"
                    value={formData.body_water}
                    onChange={(e) => updateField('body_water', e.target.value)}
                    className="bg-zinc-900/50 border-zinc-800"
                  />
                </div>
              </div>
              
              <p className="text-xs text-muted-foreground">
                * Campos obligatorios. Los demás datos los puedes obtener con una báscula de bioimpedancia.
              </p>
            </div>
          )}

          {/* Step 2: Objetivos */}
          {step === 2 && (
            <div className="space-y-6" data-testid="onboarding-step-2">
              <div className="flex items-center gap-2 text-lg font-semibold text-foreground">
                <Target className="h-5 w-5 text-primary" />
                Tu Objetivo
              </div>
              
              {/* Objetivo principal */}
              <div className="space-y-3">
                <Label>¿Cuál es tu objetivo principal? *</Label>
                <div className="grid gap-2">
                  {GOALS.map((goal) => {
                    const Icon = goal.icon;
                    return (
                      <div
                        key={goal.value}
                        onClick={() => updateField('goal', goal.value)}
                        className={`p-3 rounded-lg border cursor-pointer transition-all ${
                          formData.goal === goal.value
                            ? 'border-primary bg-primary/10'
                            : 'border-zinc-800 bg-zinc-900/50 hover:border-zinc-700'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Icon className={`h-5 w-5 ${goal.color}`} />
                          <span className="font-medium text-foreground">{goal.label}</span>
                          {formData.goal === goal.value && (
                            <CheckCircle2 className="h-5 w-5 text-primary ml-auto" />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Días de entrenamiento */}
              <div className="space-y-3">
                <Label>¿Cuántos días puedes entrenar a la semana? *</Label>
                <div className="flex gap-2">
                  {[2, 3, 4, 5, 6].map((days) => (
                    <Button
                      key={days}
                      type="button"
                      variant={formData.training_days === String(days) ? "default" : "outline"}
                      onClick={() => updateField('training_days', String(days))}
                      className="flex-1"
                    >
                      {days}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Nivel de experiencia */}
              <div className="space-y-3">
                <Label>¿Cuál es tu nivel de experiencia? *</Label>
                <div className="grid gap-2">
                  {EXPERIENCE_LEVELS.map((level) => (
                    <div
                      key={level.value}
                      onClick={() => updateField('experience_level', level.value)}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        formData.experience_level === level.value
                          ? 'border-primary bg-primary/10'
                          : 'border-zinc-800 bg-zinc-900/50 hover:border-zinc-700'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-foreground">{level.label}</p>
                          <p className="text-xs text-muted-foreground">{level.desc}</p>
                        </div>
                        {formData.experience_level === level.value && (
                          <CheckCircle2 className="h-5 w-5 text-primary" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Acceso a gimnasio */}
              <div className="space-y-3">
                <Label>¿Tienes acceso a gimnasio?</Label>
                <div className="grid gap-2">
                  {GYM_ACCESS.map((access) => (
                    <div
                      key={access.value}
                      onClick={() => updateField('gym_access', access.value)}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        formData.gym_access === access.value
                          ? 'border-primary bg-primary/10'
                          : 'border-zinc-800 bg-zinc-900/50 hover:border-zinc-700'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-foreground">{access.label}</p>
                          <p className="text-xs text-muted-foreground">{access.desc}</p>
                        </div>
                        {formData.gym_access === access.value && (
                          <CheckCircle2 className="h-5 w-5 text-primary" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Patologías */}
          {step === 3 && (
            <div className="space-y-6" data-testid="onboarding-step-3">
              <div className="flex items-center gap-2 text-lg font-semibold text-foreground">
                <AlertTriangle className="h-5 w-5 text-amber-400" />
                Salud y Limitaciones
              </div>
              
              <div className="space-y-3">
                <Label>¿Tienes alguna condición médica o lesión?</Label>
                <div className="grid gap-2">
                  {PATHOLOGIES.map((pathology) => (
                    <div
                      key={pathology.value}
                      onClick={() => togglePathology(pathology.value)}
                      data-testid={`pathology-${pathology.value}`}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        formData.pathologies.includes(pathology.value)
                          ? pathology.special 
                            ? pathology.value === 'tep'
                              ? 'border-blue-500 bg-blue-500/10'
                              : 'border-pink-500 bg-pink-500/10' 
                            : 'border-primary bg-primary/10'
                          : 'border-zinc-800 bg-zinc-900/50 hover:border-zinc-700'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Checkbox 
                          checked={formData.pathologies.includes(pathology.value)}
                          className="pointer-events-none"
                        />
                        <div className="flex-1">
                          <span className="text-foreground">{pathology.label}</span>
                          {pathology.special && (
                            <p className="text-xs text-pink-400 mt-0.5">
                              Plan especializado disponible
                            </p>
                          )}
                        </div>
                        {pathology.special && formData.pathologies.includes(pathology.value) && (
                          <span className={`text-xs px-2 py-1 rounded ${
                            pathology.value === 'tep' 
                              ? 'bg-blue-500/20 text-blue-400' 
                              : 'bg-pink-500/20 text-pink-400'
                          }`}>
                            {pathology.value === 'tep' ? 'Plan TEP' : 'Plan Ana'}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Mensaje especial si selecciona endometriosis */}
              {formData.pathologies.includes('endometriosis') && (
                <div className="p-4 rounded-lg border border-pink-500/30 bg-pink-500/5">
                  <div className="flex gap-3">
                    <Heart className="h-5 w-5 text-pink-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-pink-200 font-medium">Plan Especializado</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Al completar el onboarding, se te asignará automáticamente el plan 
                        <strong className="text-pink-300"> "Ana · Endometriosis · Base y Control"</strong>, 
                        diseñado específicamente para tu situación.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Mensaje especial si selecciona TEP */}
              {formData.pathologies.includes('tep') && (
                <div className="p-4 rounded-lg border border-blue-500/30 bg-blue-500/5">
                  <div className="flex gap-3">
                    <Heart className="h-5 w-5 text-blue-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-blue-200 font-medium">Plan Especializado TEP</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Al completar el onboarding, se te asignará automáticamente el plan 
                        <strong className="text-blue-300"> "TEP · Recuperación y Control"</strong>, 
                        diseñado para tu recuperación segura y progresiva.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label>Notas adicionales para tu entrenador (opcional)</Label>
                <Textarea
                  placeholder="Describe cualquier otra condición, lesión antigua, o información que consideres relevante..."
                  value={formData.pathology_notes}
                  onChange={(e) => updateField('pathology_notes', e.target.value)}
                  className="bg-zinc-900/50 border-zinc-800 min-h-[100px]"
                />
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between pt-4 border-t border-border">
            {step > 1 ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep(step - 1)}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Anterior
              </Button>
            ) : (
              <div></div>
            )}
            
            {step < totalSteps ? (
              <Button
                type="button"
                onClick={() => setStep(step + 1)}
                disabled={!canProceed()}
              >
                Siguiente
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={loading}
                className="bg-primary hover:bg-primary/90"
              >
                {loading ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Guardando...</>
                ) : (
                  <><CheckCircle2 className="h-4 w-4 mr-2" /> Completar</>
                )}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ClientOnboarding;
